import { Box, TableProps, Text } from '@chakra-ui/react';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { memo, useMemo } from 'react';
import { FEATURE_FLAGS } from '~/constants';
import { getMainPlatformData } from '~/core/utils';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { ISearchContentResult } from '~/searchApi/content';
import { ICreator } from '~/types';

import { Table } from '../Table/Table';
import { getColumns } from './SearchContentTable.utils';
import { ColumnKeyword } from './columns';
import { ContentCell } from './components/ContentCell';
import { PlatformIcon } from './components/PlatformIcon';
import { TextCell } from './components/TextCell/TextCell';

interface Props extends TableProps {
  contents: ISearchContentResult[];
  onClickItem?: (content: ISearchContentResult) => void;
  platforms?: string[];
  query?: string;
}

export const SearchContentTable: React.FC<Props> = memo(
  ({ contents, onClickItem, platforms, query, ...props }) => {
    const columnHelper = createColumnHelper<ISearchContentResult>();
    const publishedAtColumnEnabled =
      useFeatureFlag(FEATURE_FLAGS.foamContentSearchPostedAtColumn) ===
      'enabled';

    const defaultColumns: ColumnDef<ISearchContentResult, string>[] = useMemo(
      () => [
        // Preview
        columnHelper.accessor((row) => row.contentId, {
          id: 'preview',
          cell: ({ row }) => (
            <ContentCell
              content={row.original}
              onClick={onClickItem}
              trackId="foam-content-preview"
            />
          ),
          header: () => null,
        }),
        // Platform
        columnHelper.accessor((row) => row.contentId, {
          id: 'platform',
          cell: ({ row }) => (
            <PlatformIcon
              platform={row.original.platform}
              trackId="foam-content-platformIcon"
            />
          ),
          header: () => null,
        }),
        // Creator name
        columnHelper.accessor((row) => row.contentId, {
          id: 'creator',
          cell: ({ row }) => {
            const { creator } = row.original;
            const mainPlatformData = creator
              ? getMainPlatformData(creator as ICreator)
              : {};

            const handleContentClick = () => {
              onClickItem?.(row.original);
            };

            return (
              <TextCell
                as={Box}
                display="inline"
                onClick={handleContentClick}
                textOverflow="ellipsis"
                _hover={{ textDecoration: 'none' }}
                noOfLines={2}
                justifyContent="center"
                paddingLeft={6}
                cursor="pointer"
                track-id="foam-content-creatorName"
              >
                {mainPlatformData.name}
              </TextCell>
            );
          },
          header: () => <Text textAlign="left">Creator Name</Text>,
        }),
      ],
      [columnHelper, onClickItem],
    );

    if (query) {
      defaultColumns.splice(2, 0, ColumnKeyword(columnHelper, { query }));
    }

    const columns = useMemo(
      () =>
        getColumns(columnHelper, publishedAtColumnEnabled, {
          platforms,
          query,
        }),
      [columnHelper, platforms, publishedAtColumnEnabled, query],
    );

    return (
      <Table
        columns={[...defaultColumns, ...columns]}
        data={contents}
        enableSorting={false}
        tableHeaderProps={{
          textAlign: 'center',
        }}
        tdProps={{
          display: 'table-cell',
          verticalAlign: 'middle',
          margin: '0 auto',
          padding: 0,
        }}
        {...props}
      />
    );
  },
);
